'use client';

import { useState, useRef, useMemo, useEffect } from 'react';
import classNames from 'classnames';

import s from './ConnectionsPage.module.scss';
import Section from '../../components/Section/Section';
import Popover from '../../components/Popover/Popover';
import Button from '../../components/Button/Button';
import Dropdown from '../../components/Dropdown/Dropdown';
import Icon from '../../components/Icon/Icon';
import Grid from '../../components/Grid/Grid';
import Box from '../../components/Box/Box';
import IconText from '../../components/IconText/IconText';

import { t } from '../../i18n';
import FeaturesCard from '../../components/FeaturesCard/FeaturesCard';
import iconType from '../../utils/icontype';
import TabArea from '../../components/TabArea/TabArea';
import SingleDataValueItem from '../../components/SingleDataValueItem';
import { formatNumberWithSuffix } from '../../utils/helpers';
import HoldingsList from '../../components/HoldingsList/HoldingsList';
import { useHoldingsFilters } from './hooks/useHoldingsFilters';
import Checkbox from '../../components/Checkbox/Checkbox';
import MultiRangeSlider from '../../components/MultiRangeSlider/MultiRangeSlider';
import SimpleColumnChart from '../../components/SimpleColumnChart/SimpleColumnChart';
import dayjs from 'dayjs';
import Bookmark from '../../components/Bookmark/Bookmark';
import { useSearchParams } from 'next/navigation';

export const CompanyTab = ({
    features = {},
    holdings = [],
    isLoading = false,
    isLoadingMore = false,
    error = null,
    hasMore = false,
    activeHoldingId = null,
    setActiveHolding,
    defaultActiveHolding = null,
    selectedCountry = null,
    relativeUrl = '',
    activeCountry,
}) => {
    const {
        categoryTypes,
        guoTypes,
        searchTerm,
        setSearchTerm,
        selectedCategories,
        setSelectedCategories,
        selectedGUOs,
        setSelectedGUOs,
        revenueRange,
        setRevenueRange,
        employeeRange,
        setEmployeeRange,
        filteredHoldings,
        highestRevenue,
        highestEmployee,
        sectorTypes,
        selectedSectors,
        setSelectedSectors,
        activeFilters,
    } = useHoldingsFilters(holdings, selectedCountry);
    const searchParams = useSearchParams();
    const holdingFromQuery = searchParams.get('holding');

    const itemRefs = useRef({});
    const detailsRef = useRef(null);
    const countryExists = !!selectedCountry;
    const selectedCompany = !!activeHoldingId;

    const allHoldingsLoaded = !isLoading && !isLoadingMore && hasMore === false;

    const activeHolding =
        holdings.find((holding) => holding.sourceId === activeHoldingId) ??
        defaultActiveHolding;

    let title = null;
    let text = null;

    if (!countryExists) {
        title = t('general.noSelectedCountryLabel');
        text = t('general.noSelectedCountryText');
    } else if (!selectedCompany && holdings.length > 0) {
        title = t('general.noSelectedCompanyLabel');
        text = t('general.noSelectedCompanyText');
    }

    useEffect(() => {
        if (holdingFromQuery) {
            setActiveHolding(holdingFromQuery);
        }
    }, [holdingFromQuery, setActiveHolding]);

    useEffect(() => {
        if (activeHoldingId && detailsRef.current) {
            const offset = 130;
            const top =
                detailsRef.current.getBoundingClientRect().top +
                window.scrollY -
                offset;

            window.scrollTo({
                top,
                behavior: 'smooth',
            });
        }
    }, [activeHoldingId]);

    const countryIso = activeHolding?.countryCode
        ? activeHolding.countryCode.toLowerCase()
        : '';
    const networkPageLink = `${relativeUrl}${countryIso}/network/?holding=${activeHolding?.sourceId ?? ''}`;

    return (
        <Section noMarginBottom isMainSection fullHeight>
            <div
                className={classNames(s.LayoutWrapper, {
                    [s.ActiveCompany]: selectedCompany,
                    [s.HasNetwork]:
                        selectedCompany && activeHolding?.hasNetwork,
                })}>
                <div className={s.Left}>
                    <Filters
                        categoryTypes={categoryTypes}
                        guoTypes={guoTypes}
                        selectedCategories={selectedCategories}
                        setSelectedCategories={setSelectedCategories}
                        selectedGUOs={selectedGUOs}
                        setSelectedGUOs={setSelectedGUOs}
                        revenueRange={revenueRange}
                        setRevenueRange={setRevenueRange}
                        employeeRange={employeeRange}
                        setEmployeeRange={setEmployeeRange}
                        highestRevenue={highestRevenue}
                        highestEmployee={highestEmployee}
                        searchTerm={searchTerm}
                        setSearchTerm={setSearchTerm}
                        sectorTypes={sectorTypes}
                        selectedSectors={selectedSectors}
                        setSelectedSectors={setSelectedSectors}
                        selectedCountry={selectedCountry}
                        activeFilters={activeFilters}
                    />
                    <div className={s.Container}>
                        <Grid modifier="WithGapLarge">
                            {title && text && (
                                <Box modifiers={['White', 'ConnectionsPage']}>
                                    <IconText
                                        title={title}
                                        text={text}
                                        icon={'captive_portal'}
                                    />
                                </Box>
                            )}
                            {selectedCountry && (
                                <HoldingsList
                                    holdings={filteredHoldings}
                                    activeHoldingId={activeHoldingId}
                                    setActiveHolding={setActiveHolding}
                                    itemRefs={itemRefs}
                                    modifiers={['ConnectionsPage']}
                                    allHoldingsLoaded={allHoldingsLoaded}
                                />
                            )}
                            {isLoadingMore && (
                                <Box modifiers={['White', 'ConnectionsPage']}>
                                    <IconText
                                        title="Loading more holdings..."
                                        text="Fetching additional data..."
                                        icon={'sync'}
                                    />
                                </Box>
                            )}
                            {/* {!hasMore && holdings.length > 0 && !isLoading && (
                                <Box modifiers={['White', 'ConnectionsPage']}>
                                    <IconText
                                        title="All holdings loaded"
                                        text={`Showing ${holdings.length} holdings`}
                                        icon={'check_circle'}
                                    />
                                </Box>
                            )} */}
                        </Grid>
                    </div>
                </div>
                <div className={s.Right}>
                    {!selectedCompany || !activeHolding ? (
                        <>
                            <div className={s.Header}>
                                <h2 className={s.Title}>
                                    {features.title || t('general.features')}
                                </h2>
                            </div>
                            <div className={s.Container}>
                                <FeaturesCard features={features} />
                            </div>
                        </>
                    ) : (
                        <>
                            <div className={s.Header}>
                                <h2 className={s.Title}>
                                    {activeHolding.name}
                                </h2>
                                <div className={s.Buttons}>
                                    <Bookmark
                                        url={`${relativeUrl}${countryIso}/?holding=${activeHolding.sourceId}`}
                                        isSmall
                                        modifiers={['Map']}
                                        alternativeTitle={activeHolding.name}
                                        categories={[
                                            'Connections',
                                            activeCountry?.name,
                                        ]}
                                    />
                                    <Button
                                        title={t('general.close')}
                                        modifier="Secondary"
                                        size="Small"
                                        icon="PlusWhite"
                                        onClick={() => setActiveHolding(null)}
                                    />
                                </div>
                            </div>
                            <div className={s.Container} ref={detailsRef}>
                                <CompanyDetails holding={activeHolding} />
                            </div>
                        </>
                    )}
                    {selectedCompany && !!activeHolding?.hasNetwork && (
                        <div className={s.RightFooter}>
                            <Button
                                url={networkPageLink}
                                title={t(
                                    'connectionsPage.holdings.footerButton'
                                )}
                                modifier="Primary"
                                icon="Arrow"
                                isFullWidth
                            />
                        </div>
                    )}
                </div>
            </div>
        </Section>
    );
};

const CompanyDetails = ({ holding }) => {
    const [activeItem, setActiveItem] = useState(0);

    const people =
        holding?.contactNames?.map((fullName, index) => ({
            fullName,
            role: holding.contactRoles?.[index] || '',
            department: holding.contactDepartments?.[index] || '',
            company: holding.name,
        })) ?? [];

    const departments = people
        ? [...new Set(people?.flatMap((p) => p.department))]
        : [];

    const depAndRolesItems =
        departments?.map((dep) => ({
            tabTitle: dep,
            department: dep,
        })) ?? [];

    const activeDepartment = depAndRolesItems[activeItem]?.department;
    const filteredPeople = people?.filter(
        (person) =>
            (Array.isArray(person.otherRoles) &&
                person.otherRoles.includes(activeDepartment)) ||
            person.department === activeDepartment
    );
    const formattedTotalAssets = formatNumberWithSuffix(holding.totalAssets);

    const revenueSeriesData =
        holding.revenueSeriesYears?.map((year, i) => ({
            name: year,
            y: +(holding.revenueSeries?.[i] / 1e6).toFixed(2) ?? 0,
        })) ?? [];

    const revenueChartSeries = [
        {
            name: t('connectionsPage.holdings.revenue'),
            data: revenueSeriesData,
        },
    ];

    return (
        <div className={s.CompanyDetails}>
            <Grid modifier="WithGapLarge" columns={1}>
                <div className={classNames(s.GridWrapper, s.ThreeColumns)}>
                    {holding.globalUltimateOwner && (
                        <Box
                            modifiers={['ConnectionsPage']}
                            title={t('connectionsPage.holdings.guo')}>
                            <h2 className={s.BoxTitle}>
                                {holding.globalUltimateOwner}
                            </h2>
                        </Box>
                    )}
                    {holding.ultimateBeneficialOwners && (
                        <Box
                            modifiers={['ConnectionsPage']}
                            title={t('connectionsPage.holdings.oub')}>
                            <h2 className={s.BoxTitle}>
                                {holding.ultimateBeneficialOwners}
                            </h2>
                        </Box>
                    )}
                </div>
                {holding.fullAddress && (
                    <Grid modifier="WithGapLarge" columns={1}>
                        <Box
                            modifiers={['ConnectionsPage']}
                            title={t('connectionsPage.holdings.address')}>
                            <p className={s.Address}>{holding.fullAddress}</p>
                            <div className={s.AddressIconWrapper}>
                                <div className={s.AddressIcon}>
                                    <Icon name={iconType(holding.category)} />
                                </div>
                                <span className={s.AddressLabel}>
                                    {holding.category}
                                </span>
                            </div>
                        </Box>
                    </Grid>
                )}
                <div className={classNames(s.GridWrapper, s.TwoColumns)}>
                    {!!holding.subsidiaryCount && (
                        <Box modifiers={['ConnectionsPage']}>
                            <SingleDataValueItem
                                title={t(
                                    'connectionsPage.holdings.numberOfSubsidiaries'
                                )}
                                value={holding.subsidiaryCount}
                                unit="subsidiaries"
                                description={`(${holding.lastAccountsYear})`}
                            />
                        </Box>
                    )}

                    {holding.employeeCount !== null &&
                        holding.employeeCount > 0 && (
                            <Box modifiers={['ConnectionsPage']}>
                                <SingleDataValueItem
                                    title={t(
                                        'connectionsPage.holdings.totalEmployees'
                                    )}
                                    value={holding.employeeCount}
                                    unit="employees"
                                    description={`(${holding.lastAccountsYear})`}
                                />
                            </Box>
                        )}
                </div>
                <div className={classNames(s.GridWrapper, s.TwoColumns)}>
                    {holding.legalForm && (
                        <Box
                            modifiers={['ConnectionsPage']}
                            title={t('connectionsPage.holdings.legalForm')}>
                            <p className={s.BoxText}>{holding.legalForm}</p>
                        </Box>
                    )}
                    {holding.foundedDate && (
                        <Box
                            modifiers={['ConnectionsPage']}
                            title={t(
                                'connectionsPage.holdings.incorporationDate'
                            )}>
                            <p className={s.BoxText}>
                                {dayjs(holding.foundedDate).format(
                                    'D MMM YYYY'
                                )}
                            </p>
                        </Box>
                    )}
                </div>
                {holding.revenueSeriesYears?.length > 0 && (
                    <div className={classNames(s.GridWrapper)}>
                        <SimpleColumnChart
                            title={t('connectionsPage.holdings.revenue')}
                            xAxisLabel="Year"
                            yAxisLabel="m USD"
                            series={revenueChartSeries}
                        />
                    </div>
                )}
                {holding.totalAssets && (
                    <Grid modifier="WithGapLarge" columns={1}>
                        <Box modifiers={['ConnectionsPage']}>
                            <SingleDataValueItem
                                title={t(
                                    'connectionsPage.holdings.totalAssets'
                                )}
                                value={formattedTotalAssets.value}
                                unit={formattedTotalAssets.suffix}
                                description={`(${holding.lastAccountsYear})`}
                            />
                        </Box>
                    </Grid>
                )}
                <div className={classNames(s.GridWrapper, s.TwoColumns)}>
                    {holding.businessDescription && (
                        <Box
                            modifiers={['ConnectionsPage']}
                            title={t(
                                'connectionsPage.holdings.tradeDescription'
                            )}>
                            <p className={s.BoxText}>
                                {holding.businessDescription}
                            </p>
                        </Box>
                    )}
                    {holding.sector && (
                        <Box
                            modifiers={['ConnectionsPage']}
                            title={t('connectionsPage.holdings.sector')}>
                            <p className={s.BoxText}>{holding.sector}</p>
                        </Box>
                    )}
                </div>
                {filteredPeople?.length > 0 && (
                    <Grid modifier="WithGapLarge" columns={1}>
                        <Box
                            modifiers={['ConnectionsPage']}
                            title={t('connectionsPage.holdings.people')}>
                            <TabArea
                                items={depAndRolesItems.map((item, index) => ({
                                    title: item.tabTitle,
                                    buttonId: index,
                                }))}
                                isLink={false}
                                activeItem={activeItem}
                                setActiveItem={setActiveItem}
                            />
                            <div className={s.TabContent}>
                                {filteredPeople.map((person, index) => (
                                    <div
                                        key={index}
                                        className={s.TabContentItem}>
                                        <span className={s.TabContentItemLabel}>
                                            {person.company}
                                        </span>
                                        <h3 className={s.TabContentItemTitle}>
                                            {person.fullName}
                                        </h3>
                                        <span className={s.TabContentItemRole}>
                                            {person.role}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </Box>
                    </Grid>
                )}
            </Grid>
        </div>
    );
};
const Filters = ({
    categoryTypes,
    guoTypes,
    selectedCategories,
    setSelectedCategories,
    selectedGUOs,
    setSelectedGUOs,
    revenueRange,
    setRevenueRange,
    employeeRange,
    setEmployeeRange,
    highestRevenue,
    highestEmployee,
    searchTerm,
    setSearchTerm,
    sectorTypes,
    selectedSectors,
    setSelectedSectors,
    selectedCountry,
    activeFilters,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const popoverRef = useRef(null);

    const toggleOpen = () => setIsOpen((prev) => !prev);

    const onClearAllFilters = () => {
        setSelectedGUOs({});
        setSelectedCategories({});
        setRevenueRange([0, highestRevenue]);
        setEmployeeRange([0, highestEmployee]);
        setSelectedSectors({});

        setSearchTerm('');
    };

    const isRevenueChanged =
        revenueRange[0] !== 0 || revenueRange[1] !== highestRevenue;
    const isEmployeesChanged =
        employeeRange[0] !== 0 || employeeRange[1] !== highestEmployee;

    const isFilterApplied = useMemo(() => {
        return (
            Object.values(selectedGUOs).some(Boolean) ||
            Object.values(selectedCategories).some(Boolean) ||
            Object.values(selectedSectors).some(Boolean) ||
            isRevenueChanged ||
            isEmployeesChanged ||
            searchTerm.length > 0
        );
    }, [
        selectedGUOs,
        selectedCategories,
        isRevenueChanged,
        isEmployeesChanged,
        searchTerm,
    ]);

    return (
        <div className={s.Filter}>
            <Popover
                popoverRef={popoverRef}
                isPopoverOpen={isOpen}
                onClick={toggleOpen}
                minWidth="250px"
                modifier={'FullWidth'}
                disableLayoutAnimation
                overrideButton={
                    <div className={s.FilterButtonWrapper}>
                        <Button
                            title={isOpen ? 'Close' : 'Filter'}
                            modifier="FilterAlternative"
                            size="Small"
                            icon={isOpen ? 'FilterWhite' : 'Filter'}
                            isActive={isOpen}
                            disabled={!selectedCountry}
                            description={
                                activeFilters > 0 && `(${activeFilters})`
                            }
                        />
                    </div>
                }
                content={
                    <div className={s.PopoverContent}>
                        {sectorTypes.length > 1 && (
                            <Dropdown
                                modifiers={['BorderBottom']}
                                title={t('mapFilter.sector')}
                                showCheckmark={Object.values(
                                    selectedSectors
                                ).some(Boolean)}>
                                <ul className={s.CheckboxList}>
                                    {sectorTypes.map((item) => (
                                        <li
                                            key={item.id}
                                            className={s.CheckboxItem}>
                                            <span className={s.CheckboxLabel}>
                                                {item.label}
                                            </span>
                                            <Checkbox
                                                modifier="Alternative"
                                                value={item.id}
                                                checked={
                                                    selectedSectors[
                                                        item.value
                                                    ] || false
                                                }
                                                onChange={() =>
                                                    setSelectedSectors(
                                                        (prev) => ({
                                                            ...prev,
                                                            [item.value]:
                                                                !prev[
                                                                    item.value
                                                                ],
                                                        })
                                                    )
                                                }
                                            />
                                        </li>
                                    ))}
                                </ul>
                            </Dropdown>
                        )}

                        {categoryTypes.length > 1 && (
                            <Dropdown
                                modifiers={['BorderBottom']}
                                title={t('mapFilter.category')}
                                showCheckmark={Object.values(
                                    selectedCategories
                                ).some(Boolean)}>
                                <ul className={s.CheckboxList}>
                                    {categoryTypes.map((item) => (
                                        <li
                                            key={item.id}
                                            className={s.CheckboxItem}>
                                            <span className={s.CheckboxLabel}>
                                                {item.label}
                                            </span>
                                            <Checkbox
                                                modifier="Alternative"
                                                value={item.id}
                                                checked={
                                                    selectedCategories[
                                                        item.value
                                                    ] || false
                                                }
                                                onChange={() =>
                                                    setSelectedCategories(
                                                        (prev) => ({
                                                            ...prev,
                                                            [item.value]:
                                                                !prev[
                                                                    item.value
                                                                ],
                                                        })
                                                    )
                                                }
                                            />
                                        </li>
                                    ))}
                                </ul>
                            </Dropdown>
                        )}

                        <Dropdown
                            modifiers={['BorderBottom']}
                            title={t('mapFilter.revenue')}
                            showCheckmark={isRevenueChanged}>
                            <div className={s.DropdownContent}>
                                <MultiRangeSlider
                                    min={0}
                                    max={highestRevenue}
                                    minVal={revenueRange[0]}
                                    maxVal={revenueRange[1]}
                                    onChange={({ min, max }) =>
                                        setRevenueRange([min, max])
                                    }
                                    valueText=" USD"
                                    formatValues={true}
                                />
                            </div>
                        </Dropdown>

                        <Dropdown
                            modifiers={['BorderBottom']}
                            title={t('mapFilter.numberOfEmployees')}
                            showCheckmark={isEmployeesChanged}>
                            <div className={s.DropdownContent}>
                                <MultiRangeSlider
                                    min={0}
                                    max={highestEmployee}
                                    minVal={employeeRange[0]}
                                    maxVal={employeeRange[1]}
                                    onChange={({ min, max }) =>
                                        setEmployeeRange([min, max])
                                    }
                                    valueText=" employees"
                                />
                            </div>
                        </Dropdown>

                        <GlobalUltimateOwnerDropdown
                            guoTypes={guoTypes}
                            selectedGUOs={selectedGUOs}
                            setSelectedGUOs={setSelectedGUOs}
                        />

                        {isFilterApplied && (
                            <div className={s.FilterButtons}>
                                <button
                                    className={s.ClearAllFilters}
                                    onClick={onClearAllFilters}>
                                    {t('mapFilter.clear')}
                                    <Icon
                                        name="Close"
                                        className={s.FilterIcon}
                                    />
                                </button>
                                <div className={s.FilterSeparator} />
                                <button
                                    className={s.ClearAllFilters}
                                    onClick={() => setIsOpen(false)}>
                                    {t('mapFilter.done')}
                                </button>
                            </div>
                        )}
                    </div>
                }
            />
            <div
                className={classNames(s.Search, {
                    [s.Disabled]: !selectedCountry,
                })}>
                <div className={s.SearchInputContainer}>
                    <Icon name="Search" className={s.SearchIcon} size="Small" />
                    <input
                        type="text"
                        placeholder="Search companies"
                        className={s.SearchInput}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        disabled={!selectedCountry}
                    />
                </div>
            </div>
        </div>
    );
};

const GlobalUltimateOwnerDropdown = ({
    guoTypes = [],
    selectedGUOs = {},
    setSelectedGUOs,
}) => {
    const [searchTerm, setSearchTerm] = useState('');

    const filteredGUOs = useMemo(() => {
        return guoTypes.filter((item) =>
            item.label.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [guoTypes, searchTerm]);

    const handleChange = (value) => {
        setSelectedGUOs((prev) => ({
            ...prev,
            [value]: !prev[value],
        }));
    };

    const showCheckmark = Object.values(selectedGUOs).some(Boolean);

    return (
        <Dropdown
            modifiers={['BorderBottom']}
            title="Global Ultimate Owner"
            showCheckmark={showCheckmark}>
            <div className={s.DropdownSearchWrapper}>
                <Icon
                    name="Search"
                    className={s.DropdownSearchIcon}
                    size="Small"
                />
                <input
                    type="text"
                    placeholder="Search..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className={s.DropdownSearch}
                />
                {searchTerm.length > 0 && (
                    <button
                        className={s.DropdownClear}
                        onClick={() => setSearchTerm('')}>
                        <Icon
                            name="Close"
                            className={s.DropdownClearIcon}
                            size="Small"
                        />
                    </button>
                )}
            </div>
            <ul className={s.CheckboxListScroll}>
                {filteredGUOs.map((guo) => (
                    <li key={guo.id} className={s.CheckboxItem}>
                        <span className={s.CheckboxLabel}>{guo.label}</span>
                        <Checkbox
                            modifier="Alternative"
                            value={guo.id}
                            checked={selectedGUOs[guo.value] || false}
                            onChange={() => handleChange(guo.value)}
                        />
                    </li>
                ))}
            </ul>
        </Dropdown>
    );
};

export default GlobalUltimateOwnerDropdown;
